import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';
import {
  seasons,
  groups,
  brandOptions,
  suppliers,
  contactPersons,
  categories,
} from './stylesFixtureData';

/**
 * Fixtures for the DL-66644 epic (dl-57695, Testmo folder 93168, part 5 of 9 — multi-filter AND
 * logic, filter removal, and the empty-state-vs-error-toast contract). Real, persisted `stylesData`
 * rows — NOT `apiTransform` overrides — so the AND-composition specs exercise the genuine
 * multi-parameter `applyFilters` path (an injected/overridden row bypasses that handler entirely
 * and would make any result set look correct regardless of the filter logic under test — DL-64371).
 *
 * Additive only: every row here is appended to `stylesData` on top of the existing generated +
 * sibling-epic fixture rows. Nothing existing is edited. Ids 98300+ reserved for this epic (checked
 * empirically — the highest id in use elsewhere at time of writing is ~97714).
 *
 * `brandOptions[0]`/`brandOptions[1]` are real, per-server-instance brand ids (faker-seeded once
 * per Mirage module instantiation — one per page load) — the SAME reference the existing Brand
 * filter specs rely on (`stylesTextFilterFixtureData.ts`, `stylesLastUpdatedFixtures.ts` follow the
 * identical pattern). `brandOptions[0]` is "Brand A", `brandOptions[1]` is "Brand B" throughout —
 * their real (runtime) labels are read back off the filter dropdown in the e2e helpers, never
 * hardcoded as literal strings.
 *
 * **Divergence from the epic's literal field names** (mirrors the precedent already set by
 * `stylesTextFilterFixtureData.ts` for the `stylenumber`->`number` rename): the Testmo cases in
 * this epic describe a Status/enum value "Active" and a Custom Field named "Season". Neither
 * exists in the real app:
 *  - The real State enum (`styles.ts` STYLE_STATES) has no "Active" value — the closest, already
 *    established stand-in used throughout the sibling parts of this same epic family
 *    (`styles-filter-remove-single.spec.ts`, `read-only-table/filter-panel.spec.ts`) is the real
 *    `state = 'published'` value ("Published"). Used here for the same reason.
 *  - "Season" is a STANDARD field in this app (`params.append('season', …)`), not a Custom Field —
 *    there is no CF literally named "Season". The real, already-shipped AllowedValue custom field
 *    exercised by the equivalent CF-filter-panel case (`read-only-table/filter-panel.spec.ts`
 *    Testmo #621908) is CF 703 "Fit Type" (single-select AllowedValue: Slim/Regular/Loose) — used
 *    here in its place. This also proves DL-66652/DL-66655's claimed dependency on part 6's
 *    DL-66664 ("CF discovery and selectors") does NOT block this epic: the CF filter panel already
 *    supports selecting a CF column by its label and picking an AllowedValue, exactly like any
 *    standard `dd` filter (`generateCustomFieldFilters`, `read-only-table/filter-panel.spec.ts`).
 *
 * Row groups:
 *  - **Brand x State** (ids 98300-98306) — DL-66645 (754915), enum+enum AND 2x2 matrix.
 *  - **Brand x LastUpdated** (ids 98310-98322) — DL-66650 (754941), enum+date AND, plus four
 *    inclusive-boundary rows (all Brand A).
 *  - **Adjacent-day range** (ids 98330-98335) — DL-66653 (788296), the narrowest non-equality
 *    two-day range, all Brand A.
 *  - **Three-way: name + brand + date** (ids 98340-98351) — DL-66651 (788297), text+enum+date AND,
 *    plus four inclusive-boundary rows.
 *  - **Style Number + CF "Fit Type"** (ids 98360-98366) — DL-66652 (788298).
 *  - **Style Name + CF "Fit Type"** (ids 98370-98376) — DL-66655 (754978).
 */

let nextFixtureId = 98300;
const nextId = (): number => nextFixtureId++;

const baseRow = (overrides: Partial<StyleRow> & { name: string; customId: string }): StyleRow => ({
  id: nextId(),
  description: null,
  state: 'published',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-02-01T09:00:00',
  readyForExport: false,
  ...overrides,
});

const brandA = () => ({
  id: brandOptions[0].id,
  customId: brandOptions[0].userDefinedId ?? '',
  name: brandOptions[0].name,
  contactPersonId: contactPersons[0].id,
  contactPersonName: contactPersons[0].name,
});
const brandB = () => ({
  id: brandOptions[1].id,
  customId: brandOptions[1].userDefinedId ?? '',
  name: brandOptions[1].name,
  contactPersonId: contactPersons[2].id,
  contactPersonName: contactPersons[2].name,
});

/** CF 703 "Fit Type" allowed-value entry (single-select — see stylesCustomFieldDefinitions.ts). */
const fitType = (id: number, value: string): CustomFieldRowEntry => ({
  id: 703,
  type: 'allowedvalue',
  value: { id, value },
});
export const FIT_TYPE_REGULAR = 7032;
export const FIT_TYPE_SLIM = 7031;

// ---------------------------------------------------------------------------------------------
// DL-66645 (754915) — Brand x State 2x2 matrix. Name prefix "MFA BS".
// ---------------------------------------------------------------------------------------------
export const BS_NAMES = {
  includedOne: 'MFA BS BrandA Published One',
  includedTwo: 'MFA BS BrandA Published Two',
  brandAUnpublished: 'MFA BS BrandA Unpublished',
  brandBPublished: 'MFA BS BrandB Published',
  brandBUnpublished: 'MFA BS BrandB Unpublished',
} as const;
export const BS_INCLUDED_NAMES: readonly string[] = [BS_NAMES.includedOne, BS_NAMES.includedTwo];
export const BS_EXCLUDED_NAMES: readonly string[] = [
  BS_NAMES.brandAUnpublished,
  BS_NAMES.brandBPublished,
  BS_NAMES.brandBUnpublished,
];
/** Category 'Tops' tagged onto the included rows only, so a third Category=Tops filter (the
 * DL-66645 three-filter adversarial step) narrows to the SAME set rather than emptying it. */
const buildBrandStateRows = (): StyleRow[] => [
  baseRow({
    name: BS_NAMES.includedOne,
    customId: 'MFA-BS-001',
    brand: brandA(),
    state: 'published',
    categories: [categories[0]],
  }),
  baseRow({
    name: BS_NAMES.includedTwo,
    customId: 'MFA-BS-002',
    brand: brandA(),
    state: 'published',
    categories: [categories[0]],
  }),
  baseRow({
    name: BS_NAMES.brandAUnpublished,
    customId: 'MFA-BS-003',
    brand: brandA(),
    state: 'unpublished',
  }),
  baseRow({
    name: BS_NAMES.brandBPublished,
    customId: 'MFA-BS-004',
    brand: brandB(),
    state: 'published',
  }),
  baseRow({
    name: BS_NAMES.brandBUnpublished,
    customId: 'MFA-BS-005',
    brand: brandB(),
    state: 'unpublished',
  }),
];

// ---------------------------------------------------------------------------------------------
// DL-66650 (754941) — Brand x Last Updated. Name prefix "MFA LU". Range: 2026-01-01 -> 2026-03-27.
// ---------------------------------------------------------------------------------------------
export const LU_NAMES = {
  brandAInRange: 'MFA LU BrandA InRange',
  brandABefore: 'MFA LU BrandA Before',
  brandAAfter: 'MFA LU BrandA After',
  brandBInRange: 'MFA LU BrandB InRange',
  brandBOutOfRange: 'MFA LU BrandB OutOfRange',
  boundaryFromStart: 'MFA LU Boundary From Start',
  boundaryFromEnd: 'MFA LU Boundary From End',
  boundaryToStart: 'MFA LU Boundary To Start',
  boundaryToEnd: 'MFA LU Boundary To End',
} as const;
export const LU_INCLUDED_NAMES: readonly string[] = [
  LU_NAMES.brandAInRange,
  LU_NAMES.boundaryFromStart,
  LU_NAMES.boundaryFromEnd,
  LU_NAMES.boundaryToStart,
  LU_NAMES.boundaryToEnd,
];
export const LU_EXCLUDED_NAMES: readonly string[] = [
  LU_NAMES.brandABefore,
  LU_NAMES.brandAAfter,
  LU_NAMES.brandBInRange,
  LU_NAMES.brandBOutOfRange,
];
const buildBrandLastUpdatedRows = (): StyleRow[] => [
  baseRow({
    name: LU_NAMES.brandAInRange,
    customId: 'MFA-LU-001',
    brand: brandA(),
    lastUpdated: '2026-02-15T12:00:00',
  }),
  baseRow({
    name: LU_NAMES.brandABefore,
    customId: 'MFA-LU-002',
    brand: brandA(),
    lastUpdated: '2025-12-15T12:00:00',
  }),
  baseRow({
    name: LU_NAMES.brandAAfter,
    customId: 'MFA-LU-003',
    brand: brandA(),
    lastUpdated: '2026-04-15T12:00:00',
  }),
  baseRow({
    name: LU_NAMES.brandBInRange,
    customId: 'MFA-LU-004',
    brand: brandB(),
    lastUpdated: '2026-02-15T12:00:00',
  }),
  baseRow({
    name: LU_NAMES.brandBOutOfRange,
    customId: 'MFA-LU-005',
    brand: brandB(),
    lastUpdated: '2025-12-15T12:00:00',
  }),
  baseRow({
    name: LU_NAMES.boundaryFromStart,
    customId: 'MFA-LU-006',
    brand: brandA(),
    lastUpdated: '2026-01-01T00:00:00',
  }),
  baseRow({
    name: LU_NAMES.boundaryFromEnd,
    customId: 'MFA-LU-007',
    brand: brandA(),
    lastUpdated: '2026-01-01T23:59:59',
  }),
  baseRow({
    name: LU_NAMES.boundaryToStart,
    customId: 'MFA-LU-008',
    brand: brandA(),
    lastUpdated: '2026-03-27T00:00:00',
  }),
  baseRow({
    name: LU_NAMES.boundaryToEnd,
    customId: 'MFA-LU-009',
    brand: brandA(),
    lastUpdated: '2026-03-27T23:59:59',
  }),
];

// ---------------------------------------------------------------------------------------------
// DL-66653 (788296) — adjacent two-day range: From=2026-03-09, To=2026-03-10. All Brand A.
// ---------------------------------------------------------------------------------------------
export const ADJ_NAMES = {
  dayBeforeGuard: 'MFA ADJ Day Before Guard',
  fromStart: 'MFA ADJ From Start',
  fromEnd: 'MFA ADJ From End',
  toStart: 'MFA ADJ To Start',
  toEnd: 'MFA ADJ To End',
  dayAfterGuard: 'MFA ADJ Day After Guard',
} as const;
export const ADJ_INCLUDED_NAMES: readonly string[] = [
  ADJ_NAMES.fromStart,
  ADJ_NAMES.fromEnd,
  ADJ_NAMES.toStart,
  ADJ_NAMES.toEnd,
];
export const ADJ_EXCLUDED_NAMES: readonly string[] = [
  ADJ_NAMES.dayBeforeGuard,
  ADJ_NAMES.dayAfterGuard,
];
const buildAdjacentDayRows = (): StyleRow[] => [
  baseRow({
    name: ADJ_NAMES.dayBeforeGuard,
    customId: 'MFA-ADJ-001',
    lastUpdated: '2026-03-08T23:59:59',
  }),
  baseRow({
    name: ADJ_NAMES.fromStart,
    customId: 'MFA-ADJ-002',
    lastUpdated: '2026-03-09T00:00:00',
  }),
  baseRow({ name: ADJ_NAMES.fromEnd, customId: 'MFA-ADJ-003', lastUpdated: '2026-03-09T23:59:59' }),
  baseRow({ name: ADJ_NAMES.toStart, customId: 'MFA-ADJ-004', lastUpdated: '2026-03-10T00:00:00' }),
  baseRow({ name: ADJ_NAMES.toEnd, customId: 'MFA-ADJ-005', lastUpdated: '2026-03-10T23:59:59' }),
  baseRow({
    name: ADJ_NAMES.dayAfterGuard,
    customId: 'MFA-ADJ-006',
    lastUpdated: '2026-03-11T00:00:00',
  }),
];

// ---------------------------------------------------------------------------------------------
// DL-66651 (788297) — three-way: name contains "Summer" + Brand A + LastUpdated in
// [2026-01-01, 2026-03-31]. Name prefix "MFA3".
// ---------------------------------------------------------------------------------------------
export const THREE_WAY_NAMES = {
  matchOne: 'MFA3 Summer Match One',
  matchTwo: 'MFA3 Summer Match Two',
  wrongName: 'MFA3 Winter Coat',
  wrongBrand: 'MFA3 Summer BrandB',
  beforeRange: 'MFA3 Summer Early',
  afterRange: 'MFA3 Summer Late',
  boundaryFromStart: 'MFA3 Summer Boundary From Start',
  boundaryFromEnd: 'MFA3 Summer Boundary From End',
  boundaryToStart: 'MFA3 Summer Boundary To Start',
  boundaryToEnd: 'MFA3 Summer Boundary To End',
} as const;
export const THREE_WAY_INCLUDED_NAMES: readonly string[] = [
  THREE_WAY_NAMES.matchOne,
  THREE_WAY_NAMES.matchTwo,
  THREE_WAY_NAMES.boundaryFromStart,
  THREE_WAY_NAMES.boundaryFromEnd,
  THREE_WAY_NAMES.boundaryToStart,
  THREE_WAY_NAMES.boundaryToEnd,
];
export const THREE_WAY_EXCLUDED_NAMES: readonly string[] = [
  THREE_WAY_NAMES.wrongName,
  THREE_WAY_NAMES.wrongBrand,
  THREE_WAY_NAMES.beforeRange,
  THREE_WAY_NAMES.afterRange,
];
const buildThreeWayRows = (): StyleRow[] => [
  baseRow({
    name: THREE_WAY_NAMES.matchOne,
    customId: 'MFA3-001',
    brand: brandA(),
    lastUpdated: '2026-02-15T12:00:00',
  }),
  baseRow({
    name: THREE_WAY_NAMES.matchTwo,
    customId: 'MFA3-002',
    brand: brandA(),
    lastUpdated: '2026-02-20T12:00:00',
  }),
  baseRow({
    name: THREE_WAY_NAMES.wrongName,
    customId: 'MFA3-003',
    brand: brandA(),
    lastUpdated: '2026-02-15T12:00:00',
  }),
  baseRow({
    name: THREE_WAY_NAMES.wrongBrand,
    customId: 'MFA3-004',
    brand: brandB(),
    lastUpdated: '2026-02-15T12:00:00',
  }),
  baseRow({
    name: THREE_WAY_NAMES.beforeRange,
    customId: 'MFA3-005',
    brand: brandA(),
    lastUpdated: '2025-11-01T12:00:00',
  }),
  baseRow({
    name: THREE_WAY_NAMES.afterRange,
    customId: 'MFA3-006',
    brand: brandA(),
    lastUpdated: '2026-05-01T12:00:00',
  }),
  baseRow({
    name: THREE_WAY_NAMES.boundaryFromStart,
    customId: 'MFA3-007',
    brand: brandA(),
    lastUpdated: '2026-01-01T00:00:00',
  }),
  baseRow({
    name: THREE_WAY_NAMES.boundaryFromEnd,
    customId: 'MFA3-008',
    brand: brandA(),
    lastUpdated: '2026-01-01T23:59:59',
  }),
  baseRow({
    name: THREE_WAY_NAMES.boundaryToStart,
    customId: 'MFA3-009',
    brand: brandA(),
    lastUpdated: '2026-03-31T00:00:00',
  }),
  baseRow({
    name: THREE_WAY_NAMES.boundaryToEnd,
    customId: 'MFA3-010',
    brand: brandA(),
    lastUpdated: '2026-03-31T23:59:59',
  }),
];

// ---------------------------------------------------------------------------------------------
// DL-66652 (788298) — Style Number contains "SS26" + CF "Fit Type" = Regular.
// ---------------------------------------------------------------------------------------------
export const NUM_CF_NAMES = {
  includedOne: 'MFA NumCF Included One',
  includedTwo: 'MFA NumCF Included Two',
  wrongCf: 'MFA NumCF Wrong Fit',
  wrongNumber: 'MFA NumCF Wrong Number',
  noCf: 'MFA NumCF No Fit Set',
} as const;
export const NUM_CF_INCLUDED_NAMES: readonly string[] = [
  NUM_CF_NAMES.includedOne,
  NUM_CF_NAMES.includedTwo,
];
export const NUM_CF_EXCLUDED_NAMES: readonly string[] = [
  NUM_CF_NAMES.wrongCf,
  NUM_CF_NAMES.wrongNumber,
  NUM_CF_NAMES.noCf,
];
const buildNumberCfRows = (): StyleRow[] => [
  baseRow({
    name: NUM_CF_NAMES.includedOne,
    customId: 'SS26-CF-001',
    customFields: [fitType(FIT_TYPE_REGULAR, 'Regular')],
  }),
  baseRow({
    name: NUM_CF_NAMES.includedTwo,
    customId: 'SS26-CF-002',
    customFields: [fitType(FIT_TYPE_REGULAR, 'Regular')],
  }),
  baseRow({
    name: NUM_CF_NAMES.wrongCf,
    customId: 'SS26-CF-003',
    customFields: [fitType(FIT_TYPE_SLIM, 'Slim')],
  }),
  baseRow({
    name: NUM_CF_NAMES.wrongNumber,
    customId: 'AW25-CF-100',
    customFields: [fitType(FIT_TYPE_REGULAR, 'Regular')],
  }),
  baseRow({ name: NUM_CF_NAMES.noCf, customId: 'SS26-CF-004', customFields: [] }),
];

// ---------------------------------------------------------------------------------------------
// DL-66655 (754978) — Style Name contains "Cotton" + CF "Fit Type" = Regular.
// ---------------------------------------------------------------------------------------------
export const NAME_CF_NAMES = {
  includedOne: 'MFA NameCF Cotton Tee',
  includedMidString: 'MFA NameCF Organic Cotton Blend',
  wrongCf: 'MFA NameCF Cotton Coat',
  wrongName: 'MFA NameCF Linen Shirt',
  noCf: 'MFA NameCF Cotton No Fit Set',
} as const;
export const NAME_CF_INCLUDED_NAMES: readonly string[] = [
  NAME_CF_NAMES.includedOne,
  NAME_CF_NAMES.includedMidString,
];
export const NAME_CF_EXCLUDED_NAMES: readonly string[] = [
  NAME_CF_NAMES.wrongCf,
  NAME_CF_NAMES.wrongName,
  NAME_CF_NAMES.noCf,
];
const buildNameCfRows = (): StyleRow[] => [
  baseRow({
    name: NAME_CF_NAMES.includedOne,
    customId: 'MFA-NCF-001',
    customFields: [fitType(FIT_TYPE_REGULAR, 'Regular')],
  }),
  baseRow({
    name: NAME_CF_NAMES.includedMidString,
    customId: 'MFA-NCF-002',
    customFields: [fitType(FIT_TYPE_REGULAR, 'Regular')],
  }),
  baseRow({
    name: NAME_CF_NAMES.wrongCf,
    customId: 'MFA-NCF-003',
    customFields: [fitType(FIT_TYPE_SLIM, 'Slim')],
  }),
  baseRow({
    name: NAME_CF_NAMES.wrongName,
    customId: 'MFA-NCF-004',
    customFields: [fitType(FIT_TYPE_REGULAR, 'Regular')],
  }),
  baseRow({ name: NAME_CF_NAMES.noCf, customId: 'MFA-NCF-005', customFields: [] }),
];

export const buildMultiFilterFixtureStyles = (): StyleRow[] => [
  ...buildBrandStateRows(),
  ...buildBrandLastUpdatedRows(),
  ...buildAdjacentDayRows(),
  ...buildThreeWayRows(),
  ...buildNumberCfRows(),
  ...buildNameCfRows(),
];
